// AVRASTRO Studio — paints the layout engine's scene (packages/domain/src/studio-layout.ts) on a canvas with the brand
// fonts, and makes the exports: PNG and JPG with the provenance marking (IPTC digital source type in XMP / iTXt), PDF, and
// the transparent caption / brand overlays of a reel (the render worker only places them in time — no text in ffmpeg).
// Fonts: Inter and Literata (SIL Open Font License, embedding allowed; both carry Greek and Cyrillic), shipped with the app
// in fonts/ (tools/build.mjs). Without them the canvas falls back to the system's sans/serif — the studio says so.
import {
  AI_LABEL, AI_VIDEO_LABEL, MARKETING_LABEL, STUDIO_FORMATS, fitText, jpegToPdf, jpegWithXmp, layoutCreative, mandatoryFooter, pngWithText, provenanceXmp, safeZone,
  type BrandKit, type CreativeContent, type Cue, type ExportProvenance, type FontSpec, type Measure, type Scene, type StudioFormat, type StudioLang,
} from '@vero/domain';

const FAMILY = { sans: '"AvrastroSans", "Inter", "Segoe UI", "Noto Sans", Arial, sans-serif', serif: '"AvrastroSerif", "Literata", Georgia, "Noto Serif", serif' } as const;
let fonts: Promise<boolean> | null = null;
/** load the brand fonts once (true when both loaded) */
export function loadStudioFonts(): Promise<boolean> {
  fonts ??= (async () => {
    try {
      if (typeof FontFace === 'undefined' || !document.fonts) return false;
      const base = new URL('fonts/', document.baseURI).href;
      const faces = [new FontFace('AvrastroSans', `url(${base}Inter-var.woff2)`, { weight: '100 900' }), new FontFace('AvrastroSerif', `url(${base}Literata-SemiBold.woff2)`, { weight: '600' })];
      const loaded = await Promise.all(faces.map((f) => f.load().then((x) => { document.fonts.add(x); return true; }).catch(() => false)));
      return loaded.every(Boolean);
    } catch { return false; }
  })();
  return fonts;
}
const fontCss = (f: FontSpec) => `${f.weight} ${Math.round(f.size)}px ${FAMILY[f.family]}`;
/** the real widths of the brand fonts (the layout engine breaks lines with these) */
export function canvasMeasure(): Measure {
  const c = document.createElement('canvas').getContext('2d')!;
  return (text, f) => { c.font = fontCss(f); return c.measureText(text).width; };
}

export interface SceneImages { main?: CanvasImageSource | null; logo?: CanvasImageSource | null; mark?: CanvasImageSource | null }
const size = (img: CanvasImageSource): { w: number; h: number } => {
  const x = img as { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number; videoWidth?: number; videoHeight?: number };
  return { w: Number(x.naturalWidth || x.videoWidth || x.width || 1), h: Number(x.naturalHeight || x.videoHeight || x.height || 1) };
};
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, fail) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => fail(new Error('image')); i.src = src; });
}

/** paint a scene (at full export size) on a canvas */
export function paintScene(canvas: HTMLCanvasElement, sc: Scene, imgs: SceneImages, o: { transparent?: boolean } = {}) {
  canvas.width = sc.w; canvas.height = sc.h;
  const g = canvas.getContext('2d')!;
  g.clearRect(0, 0, sc.w, sc.h);
  for (const op of sc.ops) {
    if (op.t === 'rect') {
      if (o.transparent && op === sc.ops[0]) continue;
      g.save(); g.globalAlpha = op.opacity ?? 1; g.fillStyle = op.fill; g.beginPath();
      if (op.radius) g.roundRect(op.x, op.y, op.w, op.h, op.radius); else g.rect(op.x, op.y, op.w, op.h);
      g.fill(); g.restore();
    } else if (op.t === 'grad') {
      const gr = g.createLinearGradient(0, op.y, 0, op.y + op.h); gr.addColorStop(0, op.from); gr.addColorStop(1, op.to);
      g.fillStyle = gr; g.fillRect(op.x, op.y, op.w, op.h);
    } else if (op.t === 'image') {
      const img = imgs[op.ref];
      if (!img) continue;
      const s = size(img);
      if (op.fit === 'cover') {
        const k = Math.max(op.w / s.w, op.h / s.h), cw = op.w / k, ch = op.h / k;
        const fx = op.focus?.x ?? 0.5, fy = op.focus?.y ?? 0.5;
        const sx = Math.max(0, Math.min(s.w - cw, s.w * fx - cw / 2)), sy = Math.max(0, Math.min(s.h - ch, s.h * fy - ch / 2));
        g.drawImage(img, sx, sy, cw, ch, op.x, op.y, op.w, op.h);
      } else {
        const k = Math.min(op.w / s.w, op.h / s.h), w = s.w * k, h = s.h * k;
        g.drawImage(img, op.x, op.y + (op.h - h) / 2, w, h);
      }
    } else if (op.t === 'text') {
      g.save(); g.fillStyle = op.color; g.font = fontCss(op); g.textBaseline = 'top'; g.textAlign = op.align;
      // legibility over pictures: a soft shadow under light text
      if (op.role === 'headline' || op.role === 'subline' || op.role === 'footer' || op.role === 'label') { g.shadowColor = 'rgba(0,0,0,0.35)'; g.shadowBlur = Math.round(op.size * 0.25); }
      op.lines.forEach((l, i) => g.fillText(l, op.x, op.y + i * op.size * op.lineHeight + op.size * (op.lineHeight - 1) / 2));
      g.restore();
    }
  }
}

/** the scene of a creative at one format with the real fonts */
export function sceneFor(content: CreativeContent, format: StudioFormat, lang: StudioLang, brand: BrandKit): Scene {
  return layoutCreative(content, format, lang, brand, canvasMeasure());
}

const blobOf = (c: HTMLCanvasElement, type: string, q?: number) => new Promise<Blob>((ok, fail) => c.toBlob((b) => (b ? ok(b) : fail(new Error('export'))), type, q));
const bytes = async (b: Blob) => new Uint8Array(await b.arrayBuffer());
/** PNG / JPG / PDF of a painted canvas, marked with the provenance (AI Act art. 50 machine-readable marking) */
export async function exportCanvas(canvas: HTMLCanvasElement, kind: 'png' | 'jpg' | 'pdf', prov: ExportProvenance, title: string): Promise<Blob> {
  const xmp = provenanceXmp(prov);
  if (kind === 'png') return new Blob([pngWithText(await bytes(await blobOf(canvas, 'image/png')), { 'XML:com.adobe.xmp': xmp, Description: prov.label ?? '', Software: 'AVRASTRO Studio' }) as BlobPart], { type: 'image/png' });
  const jpg = jpegWithXmp(await bytes(await blobOf(canvas, 'image/jpeg', 0.92)), xmp);
  if (kind === 'jpg') return new Blob([jpg as BlobPart], { type: 'image/jpeg' });
  return new Blob([jpegToPdf(jpg, canvas.width, canvas.height, { title, subject: prov.label ?? 'AVRASTRO Studio', keywords: prov.models.join(', ') }) as BlobPart], { type: 'application/pdf' });
}

// --- reel overlays (transparent, full frame) -----------------------------------------------------------------------------
type ReelFormat = '9:16' | '16:9';
const frame = (format: ReelFormat) => { const { w, h } = STUDIO_FORMATS[format]; const c = document.createElement('canvas'); c.width = w; c.height = h; return { c, g: c.getContext('2d')!, w, h }; };
/** one caption, set by the layout engine's rules, in the lower part of the safe zone */
export async function captionOverlay(cue: Cue, format: ReelFormat, accent: string): Promise<Blob> {
  const { c, g, w } = frame(format);
  const z = safeZone(format);
  const m = canvasMeasure();
  const ft = fitText(cue.text, { w: z.w - 80, h: format === '9:16' ? 220 : 160 }, { family: 'sans', weight: 700, maxSize: format === '9:16' ? 62 : 54, minSize: 34, lineHeight: 1.2, maxLines: 2 }, m);
  const boxH = ft.height + 48, boxW = Math.min(z.w, Math.max(...ft.lines.map((l) => m(l, { family: 'sans', weight: 700, size: ft.size }))) + 80);
  const y = z.y + z.h - boxH - (format === '9:16' ? 120 : 40);
  g.fillStyle = 'rgba(6,27,45,0.72)'; g.beginPath(); g.roundRect((w - boxW) / 2, y, boxW, boxH, 24); g.fill();
  g.fillStyle = accent; g.fillRect((w - boxW) / 2 + 24, y + boxH - 8, 64, 4);
  g.fillStyle = '#ffffff'; g.font = fontCss({ family: 'sans', weight: 700, size: ft.size }); g.textAlign = 'center'; g.textBaseline = 'top';
  ft.lines.forEach((l, i) => g.fillText(l, w / 2, y + 24 + i * ft.size * 1.2));
  return blobOf(c, 'image/png');
}
/** the brand chrome for the whole reel: logo, marketing label, AI label when AI footage is used */
export async function chromeOverlay(format: ReelFormat, lang: StudioLang, logo: CanvasImageSource | null, ai: boolean): Promise<Blob> {
  const { c, g } = frame(format);
  const z = safeZone(format);
  if (logo) { const s = size(logo); const h = 72, w = Math.min(260, (s.w / s.h) * h); g.drawImage(logo, z.x, z.y, w, h); }
  const label = (t: string, y: number) => {
    g.font = fontCss({ family: 'sans', weight: 600, size: 26 }); const tw = g.measureText(t).width + 32;
    g.fillStyle = 'rgba(0,0,0,0.55)'; g.beginPath(); g.roundRect(z.x + z.w - tw, y, tw, 44, 22); g.fill();
    g.fillStyle = '#fff'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(t, z.x + z.w - tw / 2, y + 23);
  };
  label(MARKETING_LABEL[lang], z.y);
  if (ai) label(AI_VIDEO_LABEL[lang], z.y + 56);
  return blobOf(c, 'image/png');
}
/** the end card: call to action, contact and the mandatory note (as the images carry it) */
export async function endOverlay(format: ReelFormat, lang: StudioLang, o: { cta: string; contact: string; avrastro: boolean; accent: string }): Promise<Blob> {
  const { c, g, w, h } = frame(format);
  const z = safeZone(format);
  const m = canvasMeasure();
  g.fillStyle = 'rgba(6,27,45,0.86)'; g.fillRect(0, 0, w, h);
  const cta = fitText(o.cta, { w: z.w, h: 260 }, { family: 'serif', weight: 600, maxSize: 84, minSize: 44, lineHeight: 1.1, maxLines: 3 }, m);
  g.fillStyle = '#ffffff'; g.textAlign = 'center'; g.textBaseline = 'top'; g.font = fontCss({ family: 'serif', weight: 600, size: cta.size });
  const top = z.y + z.h * 0.3;
  cta.lines.forEach((l, i) => g.fillText(l, w / 2, top + i * cta.size * 1.1));
  g.fillStyle = o.accent; g.fillRect(w / 2 - 60, top + cta.height + 30, 120, 8);
  g.fillStyle = '#e8eef4'; g.font = fontCss({ family: 'sans', weight: 600, size: 38 });
  if (o.contact) g.fillText(o.contact, w / 2, top + cta.height + 70);
  const note = fitText(mandatoryFooter(lang, { avrastro: o.avrastro }), { w: z.w, h: 120 }, { family: 'sans', weight: 500, maxSize: 28, minSize: 20, lineHeight: 1.25, maxLines: 3 }, m);
  g.font = fontCss({ family: 'sans', weight: 500, size: note.size }); g.fillStyle = '#c9d3de';
  note.lines.forEach((l, i) => g.fillText(l, w / 2, z.y + z.h - note.height + i * note.size * 1.25));
  return blobOf(c, 'image/png');
}
export { AI_LABEL };
